//core
import React, { useState, useCallback, useEffect } from "react";
import { useSelector } from "react-redux";
import { getSymbolFromCode } from "currency-code-symbol-map";
import { Box, IconButton, Typography } from "@mui/material";

//styles
import { useStyles } from "./styles";

//hooks
import { useUser } from "../../../../../../../store/common/user/useUser";
import { useAgents as useAdminAgents } from "../../../../../../../store/admin/agents/useAgents";
import { useAgents } from "../../../../../../../store/agent/agents/useAgents";

//selectors
import { selectAgentBalanceData } from "../../../../../../../store/agent/agents/selectors";
import { selectAgentBalanceData as selectAdmAgentBalanceData } from "../../../../../../../store/admin/agents/selectors";

//components
import FloatFormat from "../../../FloatFormat";
import RefreshIcon from "@mui/icons-material/Refresh";
import PermissionsWrapper from "../../../../../PermissionsWrapper";

interface IWalletProps {
  wallet: any;
  agentId: number;
}

const Wallet: React.FC<IWalletProps> = ({
  wallet,
  agentId,
}): React.ReactElement => {
  const styles = useStyles();

  const { user } = useUser();
  const { getAgentBalanceById, clearAgentBalance } = useAgents();
  const adminAgents = useAdminAgents();

  const agentWalletBalance = useSelector(selectAgentBalanceData);
  const adminWalletBalance = useSelector(selectAdmAgentBalanceData);

  const [currentBalance, setCurrentBalance] = useState<string>(wallet.balance);

  const permissions =
    user.user.namespace === "admin"
      ? [
          "api3.admin.agentcontroller.agent_balance_update",
          "api3.admin.agentfullcontroller.agent_balance_update_all",
        ]
      : ["api3.agent.agentcontroller.agent_balance_update"];

  useEffect(() => {
    if (!!agentWalletBalance) {
      if (
        agentWalletBalance.agentId === agentId &&
        agentWalletBalance.payload.brand === wallet.brand &&
        agentWalletBalance.payload.currency === wallet.currency &&
        agentWalletBalance.payload.operator === wallet.operator
      ) {
        setCurrentBalance(agentWalletBalance.balance);
        clearAgentBalance();
      }
    }
  }, [agentWalletBalance]);

  useEffect(() => {
    if (!!adminWalletBalance) {
      if (
        adminWalletBalance.agentId === agentId &&
        adminWalletBalance.payload.brand === wallet.brand &&
        adminWalletBalance.payload.currency === wallet.currency &&
        adminWalletBalance.payload.operator === wallet.operator
      ) {
        setCurrentBalance(adminWalletBalance.balance);
        adminAgents.clearAgentBalance();
      }
    }
  }, [adminWalletBalance]);

  const handleGetBalance = useCallback(() => {
    if (user.user.namespace === "agent") {
      getAgentBalanceById({
        agentId: agentId,
        payload: {
          operator: wallet.operator,
          brand: wallet.brand,
          currency: wallet.currency,
        },
      });
    }
    if (user.user.namespace === "admin") {
      adminAgents.getAgentBalanceById({
        agentId: agentId,
        payload: {
          operator: wallet.operator,
          brand: wallet.brand,
          currency: wallet.currency,
        },
      });
    }
  }, [agentId, wallet, user.user.namespace]);

  return (
    <Box className={styles.block}>
      <PermissionsWrapper permissions={permissions}>
        <IconButton
          className={styles.refresh}
          size="small"
          aria-label="refresh"
          onClick={handleGetBalance}
        >
          <RefreshIcon fontSize="small" />
        </IconButton>
      </PermissionsWrapper>

      <Typography className={`${styles.content} force-ltr`} component={"div"} variant="body2">
        {`${wallet.brandName} - ${wallet.currency} - `}
        {getSymbolFromCode(wallet.currency) ? (
          <Box>{getSymbolFromCode(wallet.currency)} </Box>
        ) : (
          wallet.currency
        )}
        <Box>
          <FloatFormat value={currentBalance} />
        </Box>
      </Typography>
    </Box>
  );
};

export default Wallet;
